import { useEffect, useState } from "react";
import type { RunStreamState } from "../hooks/useRunStream";
import { FriendlyNarration } from "./FriendlyNarration";
import { isRetrySafeCategory, readsFailed, runErrorHeading } from "../lib/runNotify";
import { recapIsNoOp } from "../lib/runRecap";

// Calm-minimalist run-progress presentation. The founder's single
// progress surface is the FriendlyNarration trail — a vertical path of
// phase steps that extends as the run advances, with a lightly gamified
// reward beat (slide-in steps, popped-in checkmarks). The point of long
// mid-run waits is *that the founder doesn't have to attend to them* — so
// there is one honest "here's roughly where we are" signal plus a patience
// hint, not a churning stopwatch or competing progress widgets. The raw
// engine-stage list (Ingest / Analyze / Decide / Create) and the 7-segment
// phase bar were both dropped: stage names are vocabulary the founder
// can't act on, and the bar duplicated the trail (and could never reach
// full, since the terminal phase is closed by the run-complete signal, not
// a phase transition).

export function RunProgress({
  stream,
  title,
  subtitle,
  productType,
  onRetry,
}: {
  stream: RunStreamState;
  title: string;
  // 0028: when the title is the LLM-authored short name, the
  // subtitle carries the founder's original one-liner for context.
  subtitle?: string;
  productType?: string | null;
  onRetry?: () => void;
}) {
  // 0159 F4: the eyebrow says "analysis failed" only for a run that failed. A
  // client give-up (the run may still be running, or may have succeeded) and
  // a server-reported interruption have no eyebrow: ErrorState's heading
  // carries their ruled line.
  const showEyebrow = stream.phase !== "error" || readsFailed(stream);
  return (
    <main className="flex-1 max-w-3xl mx-auto w-full px-8 py-16">
      <div className="animate-slide-up">
        {showEyebrow && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
            {productType ?? "idea"} ·{" "}
            {stream.phase === "complete" ? (
              <span className="text-ok">analysis complete</span>
            ) : stream.phase === "error" ? (
              <span className="text-danger">analysis failed</span>
            ) : (
              <span className="text-accent">running</span>
            )}
          </p>
        )}
        <h1 className="text-display font-medium text-ink-50 tracking-tightest leading-[1.05]">
          {title}
        </h1>
        {subtitle && subtitle !== title && (
          <p className="mt-3 text-[15px] text-ink-300 leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        )}

        <div className="mt-12 border-t border-ink-800 pt-10">
          {stream.phase === "connecting" && <ConnectingState />}
          {(stream.phase === "running" || stream.phase === "reconnecting") && (
            <RunningState stream={stream} />
          )}
          {stream.phase === "complete" && (
            <CompleteState stream={stream} />
          )}
          {stream.phase === "error" && <ErrorState stream={stream} onRetry={onRetry} />}
        </div>
      </div>
    </main>
  );
}

function ConnectingState() {
  return (
    <div className="flex items-baseline gap-3">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-ink-500 animate-calm-pulse" />
      <span className="text-sm text-ink-300 font-mono">connecting to stream…</span>
    </div>
  );
}

function RunningState({
  stream,
}: {
  stream: Extract<RunStreamState, { phase: "running" | "reconnecting" }>;
}) {
  const steps = stream.progress ?? [];
  const activeStage = stream.activeStage;
  const activeRow = activeStage ? stream.stages.find((s) => s.name === activeStage) : null;

  // Per-stage elapsed (0026) feeds the patience hint only — the founder
  // cares about "how long has this been chewing", not a ticking clock.
  const stageStartedAt = activeRow?.startedAt;
  const elapsed = useElapsed(stageStartedAt);
  const patience = stageStartedAt ? patienceHint(elapsed) : null;
  const isReconnecting = stream.phase === "reconnecting";
  const showRetry = stream.lastRetry && stream.lastRetry.stage === activeStage;

  return (
    <>
      {/* The narration trail is the progress surface. Before the first
          user_progress event lands, a gentle "getting started" line holds
          the space so the view never looks stalled. */}
      <div aria-live="polite">
        {steps.length > 0 ? (
          <FriendlyNarration events={steps} />
        ) : (
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center" aria-hidden="true">
              <span className="absolute inline-flex h-3.5 w-3.5 rounded-full bg-accent opacity-40 animate-calm-pulse" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            <span className="text-[15px] text-ink-100">Getting started…</span>
          </div>
        )}
      </div>

      {/* Time-aware patience subtitle (0026). Hidden on small screens
          when the long-form copy would wrap awkwardly. */}
      {patience && (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-ink-400">
          <span className={patience.longForm ? "max-sm:hidden" : ""}>{patience.text}</span>
          {patience.longForm && (
            <span className="sm:hidden">{patience.shortText}</span>
          )}
        </p>
      )}

      {/* Retry annotation: gentle, no warning color. Auto-clears in the
          hook 3s after the retry event. */}
      {showRetry && (
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-ink-300">
          trying that again…
        </p>
      )}

      {/* Reconnect indicator: small mono-caps tag. */}
      {isReconnecting && (
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-ink-400">
          lost the thread for a sec — reconnecting
        </p>
      )}

      <p className="mt-6 text-xs text-ink-400 leading-relaxed max-w-xl">
        Most runs take about 9 minutes. The page will refresh automatically when
        the analysis completes — feel free to leave or come back.
      </p>
    </>
  );
}

function CompleteState({
  stream,
}: {
  stream: Extract<RunStreamState, { phase: "complete" }>;
}) {
  const steps = stream.progress ?? [];
  const totalMs = stream.stages.reduce((acc, s) => acc + (s.durationMs ?? 0), 0);
  // 0164 N1: the recap's no-op rule (lib/runRecap) over the run
  // record's landed counts, so this headline never reads "Changes ready to
  // review." over a run the recap reads as all caught up (the S2 early exit's
  // success record carries no landed counts).
  const isNoOp = stream.status === "no_op" || recapIsNoOp(stream.questionsAdded, stream.decisionsAdded);
  const headline = isNoOp
    ? "All caught up — no new changes."
    : "Changes ready to review.";
  return (
    <>
      <div className="flex items-baseline gap-3">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-ok" />
        <span className="text-[15px] text-ink-100">{headline}</span>
        <span className="ml-auto font-mono text-xs text-ink-400 tabular-nums">
          {formatElapsed(totalMs)} total
        </span>
      </div>

      {steps.length > 0 && (
        <div className="mt-8">
          <FriendlyNarration events={steps} done />
        </div>
      )}
    </>
  );
}

function ErrorState({
  stream,
  onRetry,
}: {
  stream: Extract<RunStreamState, { phase: "error" }>;
  onRetry?: () => void;
}) {
  // The Retry gate, shared with the recurring-run RunErrorBanner. Since 0159
  // the snapshot's category is only the client's own give-up reading
  // (STREAM_UNAVAILABLE) - the run_error wire never carried an engine
  // category - so isRetrySafeCategory is never true and the Retry button
  // below is unreachable today (pre-existing; a forward item, the gate is
  // kept as is).
  const retrySafe = isRetrySafeCategory(stream.category);

  return (
    <>
      {/* The heading says what the server knows (0159 F4): a client give-up
          and a server-reported interruption have their own lines. */}
      <div className="flex items-baseline gap-3">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-danger" />
        <span className="text-[15px] text-ink-100">{runErrorHeading(stream, "Something tripped.")}</span>
      </div>

      {/* The error `category` is never shown to the founder: it is the
          client's own give-up reading, which the heading above already
          words. details is the client's own too, never the server's
          message. */}
      {stream.details && (
        <p className="mt-3 text-sm text-ink-300 leading-relaxed max-w-2xl">
          {stream.details}
        </p>
      )}

      <div className="mt-8 flex items-center gap-3">
        {retrySafe && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors"
          >
            Retry analysis
          </button>
        )}
        {/* A refund is claimed only when the server knows it landed
            (refunded === true, 0159 D14/D15). */}
        {stream.refunded === true && (
          <span className="text-xs text-ink-400 font-mono">
            {retrySafe
              ? "your credit was refunded; retry is free"
              : "your credit was refunded automatically"}
          </span>
        )}
      </div>
    </>
  );
}

// Per-stage elapsed-tick hook. Subscribes to a 1Hz tick while the
// stage is active; clears the interval if startedAt is undefined
// (e.g. when no active stage). Returns 0 when startedAt is missing
// so the UI hides the elapsed display.
function useElapsed(startedAt: number | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === undefined) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (startedAt === undefined) return 0;
  return Math.max(0, now - startedAt);
}

// Format elapsed milliseconds as "Ms" under a minute or "Mm SSs" past.
function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

// Time-aware patience subtitle (0026). Returns null until 60s elapses;
// at thresholds, returns a one-liner explaining why the wait is normal.
// At 10m+, the long-form copy is hidden on small screens (<sm:) and a
// short variant is shown instead — see RunningState's render.
function patienceHint(elapsedMs: number): { text: string; longForm: boolean; shortText?: string } | null {
  const m = Math.floor(elapsedMs / 60_000);
  if (m < 1) return null;
  if (m < 3) return { text: `${m}m`, longForm: false };
  if (m < 6) return { text: `${m}m · still thinking`, longForm: false };
  if (m < 10) return { text: `${m}m · this one's a chewy idea`, longForm: false };
  return {
    text: `${m}m · usually about 9, but bigger ideas take longer`,
    longForm: true,
    shortText: `${m}m`,
  };
}
